import { useCallback, useEffect, useState, type FC } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { Voice } from '@/features/voice/domain/models/voice'
import type { VoiceParameters } from '@/features/voice/domain/models/voice-parameters'
import { fetchVoices } from '@/features/voice/domain/use-cases/fetch-voices'
import useSpeechDialogStore from './speech-dialog.store'
import Icon from '../common/Icon'
import Filters, { type Filter } from '../common/Filters'
import VerticalScrollArea from '../common/VerticalScrollArea'
import VoiceListingEmpty from './VoiceListingEmpty'
import VoiceListingLoading from './VoiceListingLoading'
import VoiceBox from './VoiceBox'
import { SpeechContentType } from './types'
import SearchBar from '../common/SearchBar'
import { useTranslation } from 'react-i18next'
import { useVoiceFiltersStore } from '@/features/voice/store/voice-filters.store'
import ScrollSentinel from '../common/ScrollSentinel'
import ExpressiveVoiceToggle from './ExpressiveVoiceToggle.tsx'

const VoiceBoxListing: FC = () => {
  const { t } = useTranslation()
  const [voices, setVoices] = useState<Voice[]>([])
  const {
    expressive,
    selectVoice,
    selectedVoice,
    setContentType,
    selectedTab,
    setExpressive,
  } = useSpeechDialogStore()
  const { filters, searchTerm } = useVoiceFiltersStore()

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['voices', searchTerm, filters, selectedTab, expressive],
    queryFn: async ({ pageParam = 0 }) => {
      const tech =
        selectedTab === 'speech' ? ('STS' as const) : ('TTS' as const)
      const newFilters = {
        ...filters,
        ...(expressive ? { providers: ['elevenlabs'] } : {}),
        technologies: [tech],
      }
      return await fetchVoices(pageParam, newFilters, searchTerm)
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.page < lastPage.totalPages ? pages.length : undefined,
    initialPageParam: 0,
  })

  useEffect(() => {
    if (!isLoading && data?.pages) {
      const newVoices = data.pages.flatMap((page) => page.items)
      setVoices(newVoices)
    }
  }, [data?.pages, isLoading])

  const { data: parametersData } = useQuery({
    queryKey: ['voices', 'parameters', searchTerm],
    queryFn: () => fetchVoices(0, {}, searchTerm),
    staleTime: Infinity,
  })
  const parameters: VoiceParameters | undefined = parametersData?.parameters

  const noResults = !isLoading && voices.length === 0
  const showEmptyStateWithOpacity =
    voices.length === 0 && isLoading && searchTerm && searchTerm.trim() !== ''

  const onSelect = (voice: Voice) => {
    selectVoice(voice)
    setContentType(SpeechContentType.Main)
  }

  const onFilterSelected = (key: string, values: string[]) => {
    useVoiceFiltersStore.setState((state) => ({
      filters: { ...state.filters, [key]: values },
    }))
  }

  const onBottomReached = useCallback(() => {
    if (hasNextPage) fetchNextPage()
  }, [fetchNextPage, hasNextPage])

  return (
    <div className="flex flex-1 flex-col gap-4 px-5 py-4">
      <div className="flex flex-row justify-between gap-4">
        <button
          type="button"
          className="flex flex-row items-center gap-1 rounded-md p-2 font-normal text-gray-400"
          onClick={() => setContentType(SpeechContentType.Main)}
        >
          <Icon name="arrowLeft" fill="currentColor" />
          <span className="font-semibold text-white">
            {t('ls_generic_back')}
          </span>
        </button>
        <div className="flex items-center justify-end gap-3">
          <ExpressiveVoiceToggle
            checked={expressive}
            onCheckedChange={setExpressive}
            tooltipPosition="top"
          />
          <div className="relative flex w-[352px] items-center gap-2 overflow-hidden rounded-md border border-zinc-500">
            <SearchBar
              onSearch={(term) =>
                useVoiceFiltersStore.setState({ searchTerm: term })
              }
              label={t('ls_generic_search_by_name_language_tag')}
              defaultValue={searchTerm}
            />
          </div>
        </div>
      </div>
      <Filters
        onFilterSelected={onFilterSelected}
        filters={Object.entries(parameters ?? {}).map(
          ([name, values]) =>
            ({
              id: name,
              labelKey: `ls_filter_buttons_${name}`,
              canSelectAll: true,
              options: values,
              initialSelected:
                (filters[name as keyof typeof filters] as
                  | string[]
                  | undefined) ?? [],
            }) as Filter
        )}
      />
      <VerticalScrollArea className="max-h-[500px] min-h-[500px] [&_.scrollbar]:ml-1 [&_.scrollbar]:translate-x-[110%]">
        {noResults || showEmptyStateWithOpacity ? (
          <div className={showEmptyStateWithOpacity ? 'opacity-50' : ''}>
            <VoiceListingEmpty />
          </div>
        ) : (
          <div className="grid max-h-[500px] grid-cols-3 flex-col gap-4 py-1">
            {!voices.length ? (
              <VoiceListingLoading />
            ) : (
              voices.map((voice) => (
                <VoiceBox
                  key={voice.id}
                  {...voice}
                  onSelect={() => onSelect(voice)}
                  selected={voice.id === selectedVoice?.id}
                  isLoading={isLoading}
                />
              ))
            )}
            {hasNextPage && <ScrollSentinel onIntersect={onBottomReached} />}
          </div>
        )}
      </VerticalScrollArea>
    </div>
  )
}

export default VoiceBoxListing
